import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingBagIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const CategoryHero = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get('/api/categories', { headers });
      setCategories(response.data.allCategories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const featuredCategories = categories
    .filter(cat => cat.is_featured && cat.is_active && !cat.parent_id)
    .slice(0, 6);

  if (loading) {
    return (
      <div className="py-16 bg-cream-50">
        <div className="container mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-cream-300 rounded w-1/4 mx-auto"></div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-40 bg-cream-200 rounded-sm"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="py-16 bg-cream-50 border-b border-cream-400/30">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-2 tracking-wide">
            קטגוריות פופולריות
          </h2>
          <div className="w-12 h-px bg-gold-500 mx-auto mb-3" />
          <p className="text-charcoal-800/70 text-sm">
            גלו את המוצרים הטובים ביותר בכל קטגוריה
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {featuredCategories.map((category, index) => (
            <Link
              key={category.id}
              to={`/categories/${category.slug}`}
              className="group relative bg-cream-50 rounded-sm border border-cream-400/50 hover:border-gold-400/50 p-4 transition-colors duration-300"
            >
              {/* Category Image/Icon */}
              <div className="relative mb-3">
                {category.image ? (
                  <div className="w-full h-28 rounded-sm overflow-hidden bg-cream-200">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                    />
                  </div>
                ) : (
                  <div className="w-full h-28 rounded-sm bg-cream-200 flex items-center justify-center">
                    <TagIcon className="h-10 w-10 text-gold-500" />
                  </div>
                )}
              </div>

              {/* Category Info */}
              <div className="text-center">
                <h3 className="text-sm font-semibold text-charcoal-900 mb-0.5 group-hover:text-gold-600 transition-colors">
                  {category.name}
                </h3>
                {category.name_en && (
                  <p className="text-xs text-charcoal-600/60">{category.name_en}</p>
                )}
                
                {/* Features */}
                <div className="flex items-center justify-center gap-1 flex-wrap mt-1.5">
                  {category.allow_negotiations && (
                    <span className="text-xs bg-gold-100/60 text-gold-700 px-2 py-0.5 rounded-sm">
                      משא ומתן
                    </span>
                  )}
                  {category.min_order_quantity > 1 && (
                    <span className="text-xs bg-cream-300/60 text-charcoal-700 px-2 py-0.5 rounded-sm">
                      מינימום {category.min_order_quantity}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link
            to="/categories"
            className="inline-flex items-center px-6 py-2.5 bg-charcoal-900 text-cream-100 text-sm font-medium hover:bg-charcoal-800 transition-colors duration-200"
          >
            <ShoppingBagIcon className="h-4 w-4 ml-1.5" />
            צפה בכל הקטגוריות
          </Link>
        </div>

        {/* Stats Bar */}
        <div className="mt-12 border-t border-cream-400/40 pt-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-2xl font-bold text-charcoal-900 mb-0.5">
                {categories.filter(c => c.is_active).length}+
              </div>
              <div className="text-xs text-charcoal-700/60">קטגוריות פעילות</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-charcoal-900 mb-0.5">
                {categories.filter(c => c.is_featured).length}
              </div>
              <div className="text-xs text-charcoal-700/60">קטגוריות מומלצות</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-charcoal-900 mb-0.5">
                {categories.filter(c => c.allow_negotiations).length}
              </div>
              <div className="text-xs text-charcoal-700/60">זמין למשא ומתן</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-charcoal-900 mb-0.5">24/7</div>
              <div className="text-xs text-charcoal-700/60">שירות לקוחות</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CategoryHero;
